import type { CAMPAIGN_LABEL_TYPE, CampaignDraftCreatorItem } from '@/entities/campaign';
import {
  CAMPAIGN_UNSUPPORTED_PLATFORM_MESSAGE,
  isCampaignSupportedPlatform,
  validateCreatorInput,
} from '@/entities/campaign';
import { SOURCE_PLATFORM_TYPE } from '@/entities/public-filter/constants';
import type { Dispatch, SetStateAction } from 'react';
import { useCallback, useMemo, useRef } from 'react';
import { toast } from 'sonner';

import {
  emptyCreatorItem,
  getCreatorRowError,
  withFallbackCreatorRow,
} from '../../../../hooks/campaign-create-shared';
import type { CampaignCreatorValidation } from './use-campaign-creator-validation';

type UseCampaignCreatorRowsArgs = {
  creatorItems: CampaignDraftCreatorItem[];
  remainingSlots: number;
  setCreatorItems: Dispatch<SetStateAction<CampaignDraftCreatorItem[]>>;
  validation: Pick<
    CampaignCreatorValidation,
    'clearCreatorValidation' | 'resetCreatorValidation' | 'setCreatorValidationIssue'
  >;
};

export type CampaignCreatorRows = ReturnType<typeof useCampaignCreatorRows>;

export function useCampaignCreatorRows({
  creatorItems,
  remainingSlots,
  setCreatorItems,
  validation,
}: UseCampaignCreatorRowsArgs) {
  const { clearCreatorValidation, resetCreatorValidation, setCreatorValidationIssue } = validation;
  const creatorSourceRefs = useRef<Record<string, HTMLInputElement | null>>({});
  const draftCreatorItems = useMemo(
    () => creatorItems.filter((item) => item.sourceValue.trim().length > 0),
    [creatorItems],
  );

  const handleAddCreatorRow = useCallback(() => {
    const emptyRow = creatorItems.find((item) => item.sourceValue.trim().length === 0);
    if (emptyRow) return emptyRow.id;
    if (remainingSlots === 0) return undefined;

    const newRow = emptyCreatorItem();
    setCreatorItems((items) => [...items, newRow]);
    return newRow.id;
  }, [creatorItems, remainingSlots, setCreatorItems]);

  const handleCreatorSourceChange = useCallback(
    (rowId: string, sourceValue: string) => {
      clearCreatorValidation(rowId);
      setCreatorItems((items) =>
        items.map((item) => (item.id === rowId ? { ...item, sourceValue } : item)),
      );
    },
    [clearCreatorValidation, setCreatorItems],
  );

  const handleCreatorSourcePaste = useCallback(
    (rowId: string, sourceValue: string) => {
      clearCreatorValidation(rowId);

      const { classification } = validateCreatorInput(sourceValue);
      setCreatorItems((items) =>
        items.map((item) =>
          item.id === rowId
            ? {
                ...item,
                sourceValue: classification?.normalizedValue ?? sourceValue,
                platform: classification?.detectedPlatform ?? item.platform,
              }
            : item,
        ),
      );
    },
    [clearCreatorValidation, setCreatorItems],
  );

  const handleCreatorSourceBlur = useCallback(
    (rowId: string) => {
      const targetRow = creatorItems.find((item) => item.id === rowId);
      if (!targetRow?.sourceValue.trim()) {
        clearCreatorValidation(rowId);
        return;
      }

      const { classification, error } = validateCreatorInput(targetRow.sourceValue);
      const nextError =
        error ??
        (classification && !classification.detectedPlatform && !targetRow.platform
          ? 'Choose a platform for this handle.'
          : undefined);

      if (!classification || nextError) {
        setCreatorValidationIssue({
          rowId,
          message: nextError ?? 'Use a creator handle or a supported profile URL.',
        });
        toast.error(nextError ?? 'Use a creator handle or a supported profile URL.');
        return;
      }

      clearCreatorValidation(rowId);
      setCreatorItems((items) =>
        items.map((item) =>
          item.id === rowId
            ? {
                ...item,
                sourceValue: classification.normalizedValue,
                platform: classification.detectedPlatform ?? item.platform,
              }
            : item,
        ),
      );
    },
    [clearCreatorValidation, creatorItems, setCreatorItems, setCreatorValidationIssue],
  );

  const handleCreatorPlatformChange = useCallback(
    (rowId: string, platform: SOURCE_PLATFORM_TYPE) => {
      const targetRow = creatorItems.find((item) => item.id === rowId);
      if (!isCampaignSupportedPlatform(platform)) {
        setCreatorValidationIssue({
          rowId,
          message: CAMPAIGN_UNSUPPORTED_PLATFORM_MESSAGE,
        });
        return;
      }

      setCreatorItems((items) =>
        items.map((item) => (item.id === rowId ? { ...item, platform } : item)),
      );

      if (!targetRow?.sourceValue.trim()) {
        clearCreatorValidation(rowId);
        return;
      }

      const nextIssueMessage = getCreatorRowError({ ...targetRow, platform });
      if (nextIssueMessage) {
        setCreatorValidationIssue({ rowId, message: nextIssueMessage });
        return;
      }

      clearCreatorValidation(rowId);
    },
    [clearCreatorValidation, creatorItems, setCreatorItems, setCreatorValidationIssue],
  );

  const handleRemoveCreatorRow = useCallback(
    (rowId: string) => {
      clearCreatorValidation(rowId);
      setCreatorItems((items) => withFallbackCreatorRow(items.filter((item) => item.id !== rowId)));
    },
    [clearCreatorValidation, setCreatorItems],
  );

  const handleClearCreatorItems = useCallback(() => {
    resetCreatorValidation();
    setCreatorItems([emptyCreatorItem()]);
  }, [resetCreatorValidation, setCreatorItems]);

  const upsertCreatorLabel = useCallback(
    (rowId: string, label?: CAMPAIGN_LABEL_TYPE) => {
      setCreatorItems((items) =>
        items.map((item) => (item.id === rowId ? { ...item, label } : item)),
      );
    },
    [setCreatorItems],
  );

  return {
    actions: {
      blurSource: handleCreatorSourceBlur,
      changeLabel: upsertCreatorLabel,
      changePlatform: handleCreatorPlatformChange,
      changeSource: handleCreatorSourceChange,
      pasteSource: handleCreatorSourcePaste,
      remove: handleRemoveCreatorRow,
    },
    add: handleAddCreatorRow,
    clear: handleClearCreatorItems,
    items: creatorItems,
    remainingSlots,
    sourceRefs: creatorSourceRefs,
    draftCreatorItems,
  };
}
